import { useRef, useState, type MouseEvent, type ReactNode } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Button, Caption1, Field, MessageBar, MessageBarBody, Textarea, makeStyles } from '@fluentui/react-components';
import { MAX_UPDATE_REASON_LENGTH, type Person, type ProfileFact } from '@vigeo/shared';
import { PopupDialog, StatusBadge, tokens } from '@vigeo/ui';
import { updatePerson } from '@/lib/api';
import { useRefreshPerson } from './groups.js';
import { Card, FactRows, useProfileStyles } from './parts.js';
import { dayOf } from './profile.js';
import {
  APPLICATION_FIELDS, answerLines, canUseOffer, factFor, offerNote, offersFor, patchForOffer, reviewBadges, type Offer, type OfferReview,
} from './profile-answers.js';

const useStyles = makeStyles({
  stack: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS, minWidth: 0 },
  pending: {
    display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS, alignItems: 'flex-start',
    paddingBlock: tokens.spacingVerticalXS, paddingInline: tokens.spacingHorizontalS,
    backgroundColor: tokens.colorNeutralBackground2, borderRadius: tokens.borderRadiusMedium,
    border: `${tokens.strokeWidthThin} dashed ${tokens.colorNeutralStroke1}`, minWidth: 0, maxWidth: '100%',
  },
  line: { margin: 0, overflowWrap: 'anywhere' },
  lines: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXS },
  form: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM },
});

/** One of the stages an offered answer can be at, as a pill. The words carry the meaning; the tone only tints it. */
export function ReviewBadge({ review }: { readonly review: OfferReview }) {
  const { label, tone } = reviewBadges[review];
  return <StatusBadge tone={tone}>{label}</StatusBadge>;
}

/** The lines of an answer, one per entry. */
function Lines({ lines }: { readonly lines: readonly string[] }) {
  const styles = useStyles();
  return <div className={styles.lines}>{lines.map((line, index) => <p key={`${index}-${line}`} className={styles.line}>{line}</p>)}</div>;
}

/**
 * "Use the candidate's answer": an ordinary `people.update` with a reason (ADR-013 §2). It asks first, because it replaces
 * HR's value and, for an email or phone, changes where every later message goes. The server writes `person.updated` with the
 * names of the fields it changed and this reason, never the value. A save that comes back refreshes the record, and the
 * offer stops being shown by itself, since the value is now on the record.
 */
function UseAnswerButton({ person, fact, offer, label }: { readonly person: Person; readonly fact: ProfileFact; readonly offer: Offer; readonly label: string }) {
  const styles = useStyles();
  const refresh = useRefreshPerson(person.id);
  const [asking, setAsking] = useState(false);
  const [reason, setReason] = useState('');
  const opener = useRef<HTMLElement | null>(null);
  const patch = patchForOffer(fact.field, offer);
  const save = useMutation({
    mutationFn: () => {
      if (patch === null) throw new Error('There is nothing here to use.');
      return updatePerson(person.id, patch, { reason: reason.trim() });
    },
    onSuccess: async () => { await refresh(); setAsking(false); setReason(''); },
  });
  if (patch === null) return null;
  const close = () => { if (!save.isPending) { setAsking(false); setReason(''); save.reset(); } };
  const open = (event: MouseEvent<HTMLElement>) => { opener.current = event.currentTarget; setAsking(true); };
  const delivery = fact.field === 'contact.email' || fact.field === 'contact.phone';
  const reviewed = offer.review === 'reviewed' || offer.review === 'approved';
  return (
    <>
      <Button appearance="secondary" size="small" onClick={open}>Use the candidate{'’'}s answer</Button>
      <PopupDialog
        open={asking}
        title={`Use the candidate’s answer for ${label}?`}
        startOn="first"
        opener={opener}
        busy={save.isPending}
        dirty={reason.trim() !== ''}
        onClose={close}
        primary={{ label: 'Use this answer', busyLabel: 'Saving…', disabled: reason.trim() === '', onClick: () => save.mutate() }}
      >
        <div className={styles.form}>
          <Lines lines={answerLines(fact.field, offer.value)} />
          <Caption1>
            {reviewed ? 'This answer was reviewed.' : 'This answer has not been reviewed.'}
            {delivery ? ' Using it changes where messages and portal links are sent.' : ''} It replaces what is on the record now.
            The change is recorded in History with your reason, not the value.
          </Caption1>
          {save.isError ? <MessageBar intent="error"><MessageBarBody>{save.error.message}</MessageBarBody></MessageBar> : null}
          <Field label="Why are you using it?" required hint="Do not type health details or a Social Security number here.">
            <Textarea value={reason} maxLength={MAX_UPDATE_REASON_LENGTH} onChange={(_, data) => setReason(data.value)} disabled={save.isPending} />
          </Field>
        </div>
      </PopupDialog>
    </>
  );
}

/**
 * A candidate answer that is not on the record: its badge, its words, what it means, and for someone who may edit and a
 * field that lives on the person record, the way to use it. `label` names the field where several share a card.
 */
export function AnswerOffer({ person, fact, offer, canEdit, label }: {
  readonly person: Person; readonly fact: ProfileFact; readonly offer: Offer; readonly canEdit: boolean; readonly label: string;
}) {
  const styles = useStyles();
  const lines = answerLines(fact.field, offer.value);
  if (lines.length === 0) return null;
  return (
    <div className={styles.pending} aria-label={`${label}: the candidate’s answer, ${reviewBadges[offer.review].label.toLowerCase()}`}>
      <ReviewBadge review={offer.review} />
      <Lines lines={lines} />
      <Caption1>{offerNote(fact, offer)} {offer.review === 'reviewed' ? 'Reviewed' : 'Saved'} {dayOf(offer.at)}.</Caption1>
      {canEdit && canUseOffer(fact.field) ? <UseAnswerButton person={person} fact={fact} offer={offer} label={label} /> : null}
    </div>
  );
}

/** Every offer for one field, one under another. Nothing where there is none. */
export function AnswerOffers({ person, fact, canEdit, label }: {
  readonly person: Person; readonly fact: ProfileFact | undefined; readonly canEdit: boolean; readonly label: string;
}) {
  const styles = useStyles();
  const offers = offersFor(person, fact);
  if (fact === undefined || offers.length === 0) return null;
  return <div className={styles.stack}>{offers.map((offer) => <AnswerOffer key={offer.review} person={person} fact={fact} offer={offer} canEdit={canEdit} label={label} />)}</div>;
}

/**
 * One value on the profile: what the person record holds (`hr`, in words, or null), and under it every candidate answer
 * that is not on the record. The record is what the Overview, the Roster and the Edit form read, so it is what is shown as
 * the value here too.
 */
export function ValueWithAnswer({ person, fact, hr, canEdit, label }: {
  readonly person: Person; readonly fact: ProfileFact | undefined; readonly hr: ReactNode; readonly canEdit: boolean; readonly label: string;
}) {
  const styles = useStyles();
  const shared = useProfileStyles();
  const blank = hr === null || hr === undefined || hr === '';
  if (offersFor(person, fact).length === 0) return blank ? null : <>{hr}</>;
  return (
    <div className={styles.stack}>
      {blank ? <span className={shared.empty}>Not recorded</span> : hr}
      <AnswerOffers person={person} fact={fact} canEdit={canEdit} label={label} />
    </div>
  );
}

/**
 * What the hire typed that the person record has no field for: employment history, references, education and the licences
 * as typed. Read only. A reviewed answer shows as reviewed; an unreviewed one shows beside it with its badge. None of it counts
 * for or against compliance, which reads reviewed evidence only (ADR-013 §2). Not drawn when nothing was answered.
 */
export function ApplicationAnswersCard({ answers, person }: { readonly answers: readonly ProfileFact[]; readonly person: Person }) {
  const styles = useStyles();
  const shared = useProfileStyles();
  const facts = APPLICATION_FIELDS.flatMap(([field, label]) => {
    const fact = factFor(answers, field);
    return fact === undefined ? [] : [{ field, label, fact }];
  });
  if (facts.length === 0) return null;
  return (
    <Card title="From the application" aside={<Caption1 className={shared.quiet}>What the candidate typed</Caption1>}>
      <FactRows wide aria-label="From the application" facts={facts.map(({ field, label, fact }) => ({
        label,
        value: (
          <div className={styles.stack}>
            {fact.effective === undefined ? null : (
              <>
                <Lines lines={answerLines(field, fact.effective.value)} />
                <Caption1 className={shared.quiet}>Reviewed {dayOf(fact.effective.at)}.</Caption1>
              </>
            )}
            {fact.pending === undefined ? null : (
              <AnswerOffer person={person} fact={fact} offer={{ value: fact.pending.value, review: fact.pending.review, at: fact.pending.savedAt }} canEdit={false} label={label} />
            )}
          </div>
        ),
      }))} />
    </Card>
  );
}
